import { isArray, isNil, sortBy } from "lodash"
import { ModelMetadata } from "resources/models"
import { getFactorLabel } from "helpers/factors"
import { COLORS } from "helpers/analyzeConstants"
import { darken, lighten } from "helpers/colors"
import type { ContentSetMetadata } from "../content-set/types"

const IS_SELECTABLE_AS_SINGLE_FACTOR_CHECKBOX = true

// Symbol, not a string, so it can never collide with a real backend category id (unlike "other").
const UNCATEGORIZED_GROUP_KEY = Symbol("uncategorized-factor-group")
type CategoryGroupKey = string | typeof UNCATEGORIZED_GROUP_KEY

function getUncategorizedGroupId(categoriesById: Map<string, { id: string, label: string }>): string {
  return categoriesById.has("other") ? "uncategorized" : "other"
}

export interface CustomGraphItem {
  id: string
  label: string
  category?: string
  disabled: boolean
  defaultDisabled: boolean
  type: "factor"
  color?: string
}

export type CustomGraphItemDescriptor = Omit<CustomGraphItem, "disabled" | "defaultDisabled">

function buildAvailableMetricsGroups<TCategory, TFactor>(
  categories: TCategory[],
  factors: TFactor[],
  getCategoryId: (category: TCategory) => string,
  getCategoryLabel: (category: TCategory) => string,
  getFactorId: (factor: TFactor) => string,
  getFactorCategory: (factor: TFactor) => string | undefined,
  getFactorLabel: (factor: TFactor) => string,
) {
  const categoriesById = new Map<string, { id: string, label: string }>(
    categories.map((category): [string, { id: string, label: string }] => [getCategoryId(category), { id: getCategoryId(category), label: getCategoryLabel(category) }]),
  )
  const factorsByCategory = new Map<CategoryGroupKey, { id: string, label: string, singleFactor: true }[]>()

  for (const factor of factors) {
    const categoryKey: CategoryGroupKey = getFactorCategory(factor) ?? UNCATEGORIZED_GROUP_KEY
    const list = factorsByCategory.get(categoryKey) ?? []
    list.push({ id: getFactorId(factor), label: getFactorLabel(factor), singleFactor: IS_SELECTABLE_AS_SINGLE_FACTOR_CHECKBOX })
    factorsByCategory.set(categoryKey, list)
  }

  return Array.from(factorsByCategory.entries()).map(([categoryKey, items]) => {
    const groupId = categoryKey === UNCATEGORIZED_GROUP_KEY ? getUncategorizedGroupId(categoriesById) : categoryKey
    return {
      id: groupId,
      label: categoriesById.get(groupId)?.label ?? groupId,
      excludeGroupIdFromChildMetricPaths: true,
      items: sortBy(items, item => item.label.toLowerCase()),
    }
  })
}

function buildFactorItems<TFactor>(
  factors: TFactor[],
  getFactorId: (factor: TFactor) => string,
  getFactorCategory: (factor: TFactor) => string | undefined,
  getFactorLabel: (factor: TFactor) => string,
): CustomGraphItemDescriptor[] {
  return factors.map(factor => ({
    id: getFactorId(factor),
    label: getFactorLabel(factor),
    category: getFactorCategory(factor),
    type: "factor" as const,
  }))
}

export function buildCustomGraphAvailableMetrics(modelMetadata: ModelMetadata | null | undefined) {
  const factorsByIdMap = Object.fromEntries((modelMetadata?.factors ?? []).map(factor => [factor.id, factor]))
  return buildAvailableMetricsGroups(
    modelMetadata?.categories ?? [],
    modelMetadata?.factors ?? [],
    category => category.id,
    category => category.label,
    factor => factor.id,
    factor => factor.category,
    factor => getFactorLabel(factorsByIdMap, factor.id),
  )
}

export function buildAllFactorItems(modelMetadata: ModelMetadata | null | undefined): CustomGraphItemDescriptor[] {
  const factorsByIdMap = Object.fromEntries((modelMetadata?.factors ?? []).map(factor => [factor.id, factor]))
  return buildFactorItems(
    modelMetadata?.factors ?? [],
    factor => factor.id,
    factor => factor.category,
    factor => getFactorLabel(factorsByIdMap, factor.id),
  )
}

export function buildContentSetCustomGraphAvailableMetrics(contentSetMetadata: ContentSetMetadata | null | undefined) {
  return buildAvailableMetricsGroups(
    contentSetMetadata?.categories ?? [],
    contentSetMetadata?.factors ?? [],
    category => category.id,
    category => category.name,
    factor => factor.id,
    factor => factor.category,
    factor => factor.name,
  )
}

export function buildAllContentSetFactorItems(contentSetMetadata: ContentSetMetadata | null | undefined): CustomGraphItemDescriptor[] {
  return buildFactorItems(
    contentSetMetadata?.factors ?? [],
    factor => factor.id,
    factor => factor.category,
    factor => factor.name,
  )
}

const LIGHTEN_DARKEN_STEP_PER_PALETTE_CYCLE = 0.5

function getSeriesColor(index: number): string {
  const baseColor = COLORS[index % COLORS.length]
  const cycle = Math.floor(index / COLORS.length)
  if (cycle === 0) return baseColor
  const magnitude = Math.ceil(cycle / 2) * LIGHTEN_DARKEN_STEP_PER_PALETTE_CYCLE
  return cycle % 2 === 1 ? lighten(baseColor, magnitude) || baseColor : darken(baseColor, magnitude) || baseColor
}

export function makeSelectedFilterItems(sharedPanelDisabledItems: Record<string, boolean | undefined>) {
  return function filterSelectedItems(items: CustomGraphItemDescriptor[], data: unknown[]): CustomGraphItem[] {
    if (!isArray(items) || !isArray(data) || data.length === 0) return []

    // Ordinal from the unfiltered selection, not the post-filter array index — otherwise a color
    // reassigns when which items have data shifts between renders (e.g. a late-arriving factor).
    const stableOrdinalById = new Map(items.map((item, index) => [item.id, index]))
    const withData = items.filter(item => data.some(dataPoint => dataPoint != null && !isNil((dataPoint as Record<string, unknown>)[item.id])))

    return withData.map(item => ({
      ...item,
      disabled: Boolean(sharedPanelDisabledItems[item.id]),
      defaultDisabled: false,
      color: item.color || getSeriesColor(stableOrdinalById.get(item.id) ?? 0),
    }))
  }
}
